import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { IntlProvider, useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../../../utils/locale';
import translations from '../../../../lib/i18n/translations.json';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/client';
import { createConsumer } from '@rails/actioncable';
import { get } from 'lodash';
import { getAvatarStyle, isNewStyleAvatar } from '../../../../lib/s3';
import ParticipantName from '../shared/ParticipantName';
import Card from '../../../../../../ui/canon/Card';
import EmptyState from '../../../../../../ui/canon/EmptyState';
import LoadingStates from '../shared/LoadingStates';
import MessageItem from './MessageItem';
import MessageComposer from './MessageComposer';
import TypingIndicator from './TypingIndicator';
import EmptyMessages from './EmptyMessages';
import Button from '../../../../../../ui/canon/Button';
import MessageSearch from './MessageSearch';
import BlockButton from './BlockButton';
import ArchiveButton from './ArchiveButton';
import PartialSaleInfo from './PartialSaleInfo';
import RjSellerConversationActions from '../../RjVisibility/RjSellerConversationActions';
import { messageFromSocket } from './socketMessage';
import { formatMessageTime, compareDates, getTimeDifference } from '../shared/dateUtils';
import { 
  ShoppingCartIcon, 
  ChatBubbleLeftRightIcon, 
  BriefcaseIcon,
  StarIcon,
  LifebuoyIcon
} from '@heroicons/react/24/outline';

// GraphQL query for conversation details and messages
const GET_CONVERSATION = gql`
  query GetConversation($convoId: ID!) {
    getConversation(convoId: $convoId) {
      id
      participant1 {
        id
        name
        allowed_name
        user_name
        fb_user_name
        profile_picture
        is_admin
        is_student
        student_avatar_url
        advisor_firm_name
        # RJ Seller Visibility — server-computed branch for the Meeting Room
        # actions below. Both this panel and the profile drawer read THIS; neither
        # may re-derive it client-side.
        rj_meeting_room_invite_state {
          branch
          deal_id
          workflow_state
          deals { id name }
        }
      }
      participant2 {
        id
        name
        allowed_name
        user_name
        fb_user_name
        profile_picture
        is_admin
        is_student
        student_avatar_url
        advisor_firm_name
        # RJ Seller Visibility — server-computed branch for the Meeting Room
        # actions below. Both this panel and the profile drawer read THIS; neither
        # may re-derive it client-side.
        rj_meeting_room_invite_state {
          branch
          deal_id
          workflow_state
          deals { id name }
        }
      }
      conversationType
      blockedByParticipant1
      blockedByParticipant2
      archivedByParticipant1
      archivedByParticipant2
      endedAt
      endedByUserId
      ndaNudgeShownAt
      rjVisibilitySellerView
      # Partial Sale conversations — the listing owner's Meeting Room control.
      # Server-computed for the PS pair (any motivations); null/false for the
      # buyer and everyone else.
      psMeetingRoomSellerView
      psMeetingRoomInviteState {
        branch
        deal_id
        workflow_state
        partial_sale_listing_id
        deals { id name }
      }
      partialSaleListingUser {
        id
        partial_sale_listing {
          id
          segment_details
          description
          asking_price
          number_of_clients
          assets_under_management
          fee_based_revenue
          trails_revenue
          direct_revenue
          commission_revenue
          sale_status
          states
          client_demographics_details
          services_provided_details
          other_details
        }
        buyer {
          id
          name
          allowed_name
          user_name
          fb_user_name
          profile_picture
        }
        seller {
          id
          name
          allowed_name
          user_name
          fb_user_name
          profile_picture
        }
      }
      personalMessages {
        id
        body
        createdAt
        read
        messageType
        system_authored
        sender {
          id
          name
          allowed_name
          profile_picture
          is_student
          student_avatar_url
        }
        receiver {
          id
          name
          allowed_name
          profile_picture
          is_student
          student_avatar_url
        }
        documents {
          id
          name
          download_url
          mime_type
          created_at
        }
      }
    }
    getCurrentUser {
      id
      name
      allowed_name
      profile_picture
      is_admin
      company {
        id
        name
        allow_blocking_in_messages
        # PartialSaleInfo reads these flags (hide_partial_sales_purchase_price,
        # partial_sales_property_casualty); without them its gates never fire.
        feature_types
      }
    }
    company {
      id
      name
      deal_room_branded_name
      deal_room_branded_name_fr
      allow_blocking_in_messages
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;

// GraphQL mutation for sending messages with attachments
const SEND_MESSAGE_WITH_ATTACHMENTS = gql`
  mutation SendMessageWithAttachments($conversationId: ID!, $body: String, $files: [File!]) {
    sendMessageWithAttachments(conversationId: $conversationId, body: $body, files: $files) {
      id
      body
      createdAt
      read
      messageType
      sender {
        id
        name
        allowed_name
        profile_picture
      }
      receiver {
        id
        name
        allowed_name
        profile_picture
      }
      documents {
        id
        name
        download_url
        mime_type
        created_at
      }
    }
  }
`;

/**
 * ChatPanel - Professional chat interface with real-time messaging
 * 
 * Features GraphQL integration, ActionCable real-time updates, and modern UI
 */
const ChatPanel = ({ currentUser, conversationId, theme = 'light', onConversationUpdate }) => {
  const intl = useIntl();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightedMessageIndex, setHighlightedMessageIndex] = useState(-1);
  const [conversationState, setConversationState] = useState(null);
  // Send/connection failures, shown in-pane instead of via alert().
  //
  // RjSellerConversationActions moved its four alert() calls to a banner in the
  // same commit; these two were left behind, so a send failure still blocked the
  // page with a modal dialog while an invite failure rendered inline. Same
  // pattern here: persistent (alert() vanishes the moment it is dismissed, taking
  // the reason with it), announced to assistive tech, and cleared on the two
  // events that make it stale — switching threads, and trying again.
  //
  // One edge is narrower than alert() was: the banner renders below the
  // `if (!conversation)` early return, while the ActionCable subscription is
  // keyed on conversationId alone. A connection rejection arriving before the
  // query resolves is therefore held until the pane renders — and if the
  // conversation genuinely does not exist, the user sees "conversation not
  // found", which is the better message anyway.
  const [sendError, setSendError] = useState(null);



  // RJ Seller Visibility Workflow — nudge trigger arrives via ActionCable
  // socket payload (see ChatChannel#speak). Setting this to true fires the
  // dismissible modal in RjSellerConversationActions exactly once.
  const [rjNudgeTriggered, setRjNudgeTriggered] = useState(false);

  // GraphQL hooks
  const { loading, error, data, refetch } = useQuery(GET_CONVERSATION, {
    variables: { convoId: conversationId },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      console.log('[ChatPanel] GET_CONVERSATION completed:', {
        conversationId,
        hasConversation: !!data?.getConversation,
        messageCount: data?.getConversation?.personalMessages?.length || 0,
        currentUser: data?.getCurrentUser?.id
      });
    },
    onError: (error) => {
      console.error('[ChatPanel] GET_CONVERSATION error:', error);
    }
  });

  const [sendMessageWithAttachments, { loading: mutationLoading }] = useMutation(SEND_MESSAGE_WITH_ATTACHMENTS, {
    context: { hasUpload: true }
  });
  const [messageReactions, setMessageReactions] = useState(() => {
    // Load reactions from localStorage on component mount
    try {
      const saved = localStorage.getItem(`messageReactions_${conversationId}`);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading reactions from localStorage:', error);
      return {};
    }
  }); // Store reactions by message ID

  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Save reactions to localStorage whenever they change
  useEffect(() => {
    if (conversationId && Object.keys(messageReactions).length > 0) {
      try {
        localStorage.setItem(`messageReactions_${conversationId}`, JSON.stringify(messageReactions));
      } catch (error) {
        console.error('Error saving reactions to localStorage:', error);
      }
    }
  }, [messageReactions, conversationId]);
  const messagesEndRef = useRef(null);
  const consumer = useRef(null);
  const subscription = useRef(null);
  const inputRef = useRef(null);
  const typingTimeout = useRef(null);
  const messagesRef = useRef([]); // For avoiding stale closure in ActionCable

  // Initialize ActionCable connection
  useEffect(() => {
    if (!conversationId || !currentUser?.id) return;

    // Clear messages when conversation changes
    setMessages([]);
    messagesRef.current = [];
    
    // Load reactions for the new conversation
    try {
      const saved = localStorage.getItem(`messageReactions_${conversationId}`);
      setMessageReactions(saved ? JSON.parse(saved) : {});
    } catch (error) {
      console.error('Error loading reactions for new conversation:', error);
      setMessageReactions({});
    }

    consumer.current = createConsumer();
    
    subscription.current = consumer.current.subscriptions.create(
      {
        channel: 'ChatChannel',
        room_id: conversationId
      },
      {
        connected: () => {
          console.log('[ChatPanel] ActionCable connected to conversation:', conversationId);
          setIsConnected(true);
        },
        
        disconnected: () => {
          console.log('[ChatPanel] ActionCable disconnected from conversation:', conversationId);
          setIsConnected(false);
        },
        
        rejected: () => {
          console.error('[ChatPanel] ActionCable connection rejected for conversation:', conversationId);
          setSendError('Unable to connect to messaging. Please refresh the page.');
        },
        
        received: (data) => {
          console.log('[ChatPanel] ActionCable received data:', data);

          // RJ Seller Visibility Workflow — the ChatChannel#speak backend
          // broadcasts nda_nudge_triggered: true alongside the message when
          // the seller has just crossed the 5-response threshold. Flip local
          // state so RjSellerConversationActions pops the nudge modal once.
          if (data && data.nda_nudge_triggered === true) {
            setRjNudgeTriggered(true);
          }

          switch (data.type) {
            case 'message':
              // Check for duplicates using ref to avoid stale closure
              if (messagesRef.current.some(m => m.id === data.message.id)) {
                break;
              }
              
              const newMessage = messageFromSocket(data.message);       
              
              // Add all messages from ActionCable (prevents issues when GraphQL is slow)
              setMessages(prev => {
                // Check if message already exists to prevent ActionCable duplicates
                if (prev.some(m => m.id === newMessage.id)) {
                  return prev;
                }
                
                const updated = [...prev, newMessage];
                messagesRef.current = updated; // Keep ref in sync
                return updated;
              });
              
              // Use requestAnimationFrame to ensure DOM has updated before scrolling
              requestAnimationFrame(() => {
                setTimeout(() => scrollToBottom(), 50);
              });
              
              // Clear typing indicator for this user
              setTypingUsers(prev => prev.filter(user => user.id !== newMessage.sender.id));
              break;
              
            case 'messages':
              // Bulk message loading - not used since GraphQL handles initial loading
              break;
              
            case 'typing':
              // Handle typing indicator
              const typingUser = {
                id: data.user.id,
                name: data.user.name || data.user.allowed_name,
                allowed_name: data.user.allowed_name
              };
              
              if (data.typing && typingUser.id !== currentUser?.id) {
                setTypingUsers(prev => {
                  // Add user to typing list if not already there
                  if (!prev.some(user => user.id === typingUser.id)) {
                    return [...prev, typingUser];
                  }
                  return prev;
                });
              } else {
                // Remove user from typing list
                setTypingUsers(prev => prev.filter(user => user.id !== typingUser.id));
              }
              break;
              
            default:
              break;
          }
        },
        
        // Add the speak function like ChatWidget
        speak: function(data) {
          return this.perform('speak', {
            room_id: data.room_id || conversationId,
            message: data.message
          });
        },
        
        // Add the load function like ChatWidget
        load: function() {
          return this.perform('load', { room_id: conversationId });
        },
        
        // Add typing indicator function
        typing: function(data) {
          return this.perform('typing', {
            room_id: data.room_id || conversationId,
            typing: data.typing
          });
        }
      }
    );

    return () => {
      if (subscription.current) {
        subscription.current.unsubscribe();
      }
      if (consumer.current) {
        consumer.current.disconnect();
      }
    };
  }, [conversationId, currentUser?.id]);

  // Auto-scroll to bottom - only within the messages container, not the whole page
  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('.messages-scroll-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  // Scroll to bottom when conversation data loads
  useEffect(() => {
    if (data?.getConversation?.personalMessages?.length > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [data?.getConversation?.id]);

  // Scroll to bottom when conversation changes
  useEffect(() => {
    if (conversationId) {
      setTimeout(() => scrollToBottom(), 200);
    }
  }, [conversationId]);



  // Send message via ActionCable (legacy function - now using sendMessageWithData)
  const sendMessage = async () => {
  };

  // Handle typing indicators
  const handleTyping = (typing) => {
    setIsTyping(typing);
    
    if (subscription.current) {
      subscription.current.typing({
        room_id: conversationId,
        typing: typing
      });
    }
  };

  // Handle message reactions
  const handleReaction = (messageId, emoji) => {
    
    // Update local reactions state
    setMessageReactions(prev => {
      const messageReactions = prev[messageId] || [];
      const existingReaction = messageReactions.find(r => r.emoji === emoji);
      
      let updatedReactions;
      if (existingReaction) {
        // If reaction exists, increment count
        updatedReactions = messageReactions.map(r => 
          r.emoji === emoji ? { ...r, count: r.count + 1 } : r
        );
      } else {
        // Add new reaction
        updatedReactions = [...messageReactions, { emoji, count: 1 }];
      }
      
      return {
        ...prev,
        [messageId]: updatedReactions
      };
    });
    
    // Show a subtle success notification
    const notification = document.createElement('div');
    notification.innerHTML = `${emoji}`;
    notification.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: var(--succession-bg-primary);
      color: var(--succession-text-primary);
      padding: 16px 20px;
      border-radius: 50px;
      font-size: 24px;
      font-weight: 500;
      box-shadow: var(--succession-shadow-lg);
      z-index: 1000;
      animation: reactionPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      backdrop-filter: blur(10px);
      border: 2px solid var(--succession-border);
    `;
    
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes reactionPop {
        0% { 
          transform: translate(-50%, -50%) scale(0.5);
          opacity: 0;
        }
        50% { 
          transform: translate(-50%, -50%) scale(1.2);
          opacity: 1;
        }
        100% { 
          transform: translate(-50%, -50%) scale(1);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(notification);
    
    // Remove notification after animation
    setTimeout(() => {
      notification.remove();
      style.remove();
    }, 600);
    
    // TODO: Send to backend
    // In a full implementation, this would send a GraphQL mutation
    // or ActionCable message to persist the reaction
  };

  // Handle input changes
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  // Handle keyboard shortcuts
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle search functionality
  const handleSearchResult = (messageIndex) => {
    setHighlightedMessageIndex(messageIndex);
    
    // Scroll to the message
    setTimeout(() => {
      const messageElements = document.querySelectorAll('[data-message-index]');
      if (messageElements[messageIndex]) {
        messageElements[messageIndex].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Clear highlight after 3 seconds
        setTimeout(() => {
          setHighlightedMessageIndex(-1);
        }, 3000);
      }
    }, 100);
  };

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  // Handle conversation state updates (for blocking/unblocking)
  const handleConversationUpdate = (updatedConversation) => {
    setConversationState(updatedConversation);
    // Call parent callback if provided
    if (onConversationUpdate) {
      onConversationUpdate(updatedConversation);
    }
  };

  // Reset conversation state when conversationId changes
  useEffect(() => {
    if (conversationId) {
      setConversationState(null);
    }
    // Without this a failure on one thread follows the user into every other one
    // — the bug RjSellerConversationActions hit and fixed the same way.
    setSendError(null);
  }, [conversationId]);

  // 🔴 Adopt query data in an EFFECT, not during render.
  //
  // This used to run in the render body as
  // `if (data?.getConversation && !conversationState) setConversationState(...)`.
  // Two defects came out of that, both reported 2026-09-24:
  //
  //   1. `data` still holds the PREVIOUS conversation while the refetch for the
  //      new id is in flight, so the stale result was adopted immediately — and
  //      because adoption was one-shot, the correct data was never picked up. The
  //      pane rendered the old thread while the action bar submitted the new id,
  //      which is why Reopen appeared on an un-ended conversation and the server
  //      answered "Conversation is not ended".
  //   2. Setting state during render does not reliably re-render, so the pane
  //      only corrected itself after a manual page reload.
  //
  // Keyed on the DATA OBJECT, not just the id. Keying on the id alone meant a
  // refetch that changed the conversation without changing its id — exactly what
  // Stop Messaging / Reopen do to `endedAt` — never re-adopted, so the action bar
  // kept rendering the stale state and the new button only appeared after a
  // manual page reload.
  //
  // Loop-safe: Apollo returns a referentially stable object while the data is
  // unchanged, and React's useState bails out when the next value is
  // Object.is-equal to the current one, so re-running this with the same result
  // does not re-render.
  //
  // The server is the authority here. An optimistic block/archive value set by
  // handleConversationUpdate is intentionally replaced by the next refetch.
  useEffect(() => {
    const fetched = data?.getConversation;
    if (fetched && String(fetched.id) === String(conversationId)) {
      setConversationState(fetched);
    }
  }, [data?.getConversation, conversationId]);

  // Get other participant ID for this conversation
  const getOtherParticipantId = (conversationData = null) => {
    // We'll get the conversation data from the Query component's data
    // For now, we need to find another way to get the receiver ID
    // This might need to be passed from the parent component or extracted from GraphQL
    return null; // Will be fixed when we have the conversation data
  };

  // Using Safari-compatible formatMessageTime from dateUtils

  // If no conversation selected
  if (!conversationId) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--succession-bg-primary)] border-l border-[var(--succession-border)]">
        <Card variant="outlined" size="lg">
          <EmptyState
            icon={
              <svg className="w-full h-full text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
            iconSize="lg"
            title={intl.formatMessage({ id: 'succession.messaging.chat_panel.select_conversation_title' })}
            description={intl.formatMessage({ id: 'succession.messaging.chat_panel.select_conversation_description' })}
            size="md"
          />
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* CSS Animations for playful effects */}
      <style>
        {`
          @keyframes pulse-online {
            0%, 100% { 
              transform: scale(1);
              box-shadow: 0 0 10px rgba(59, 130, 246, 0.6);
            }
            50% { 
              transform: scale(1.1);
              box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
            }
          }
        `}
      </style>
      
      {/* Now using hooks instead of render props */}
      {(() => {
        // `loading && !data` — NOT `loading` alone. This query runs with
        // notifyOnNetworkStatusChange, so every refetch() flips loading true;
        // gating the skeleton on `loading` alone therefore tore down the whole
        // conversation view on each background refresh, remounting the seller
        // action bar and wiping its local state. That is why the "Invite to
        // Meeting Room" CTA survived a successful invite: the pending flag set
        // moments earlier was destroyed with the component. Apollo keeps the
        // previous `data` during a refetch, so this still shows the skeleton on
        // first load and leaves the panel mounted while refreshing.
        if (loading && !data) {
          return (
            <div className="h-full flex flex-col">
              <ChatHeader loading={true} />
              <LoadingStates.MessageList />
            </div>
          );
        }

        if (error) {
          return (
            <div className="h-full flex flex-col">
              <ChatHeader error={true} />
              <div className="flex-1 flex items-center justify-center bg-[var(--succession-bg-primary)] p-10">
                <Card variant="outlined" size="lg">
                  <EmptyState
                    variant="error"
                    icon={
                      <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                      </svg>
                    }
                    iconBg="var(--succession-error-bg)"
                    iconColor="var(--succession-error)"
                    iconSize="md"
                    title={intl.formatMessage({ id: 'succession.messaging.chat_panel.failed_to_load' })}
                    description={error.message}
                    action={
                      <Button
                        onClick={() => refetch()}
                        variant="primary"
                        size="sm"
                      >
                        {intl.formatMessage({ id: 'succession.messaging.hub.try_again' })}
                      </Button>
                    }
                    size="md"
                  />
                </Card>
              </div>
            </div>
          );
        }

        const conversation = conversationState || data?.getConversation;
        if (!conversation) {
          return (
            <div className="h-full flex items-center justify-center bg-[var(--succession-bg-primary)] p-10">
              <Card variant="outlined" size="md">
                <EmptyState
                  description={intl.formatMessage({ id: 'succession.messaging.chat_panel.conversation_not_found' })}
                  size="sm"
                />
              </Card>
            </div>
          );
        }

        // Update conversation state when GraphQL data loads (but only initially)

        // Merge GraphQL messages with real-time messages with proper deduplication
        const messageMap = new Map();
        
        // Add GraphQL messages first
        // Keyed by String(id): GraphQL ids are strings, ActionCable ids are numbers,
        // so a message delivered both ways (e.g. a refetch right after a broadcast)
        // used to render twice.
        (conversation.personalMessages || []).forEach(msg => {
          messageMap.set(String(msg.id), {
            ...msg,
            source: 'graphql'
          });
        });
        
        // Add real-time messages, but only if they don't already exist
        messages.forEach(msg => {
          if (!messageMap.has(String(msg.id))) {
            messageMap.set(String(msg.id), {
              ...msg,
              source: 'actioncable'
            });
          } else {
            console.log('🔄 Skipping duplicate message from ActionCable:', msg.id);
          }
        });
        
        // Convert back to array and sort by timestamp using Safari-compatible comparison
        const allMessages = Array.from(messageMap.values())
          .sort((a, b) => compareDates(a.createdAt, b.createdAt));

        // Use GraphQL currentUser data (has company.allow_blocking_in_messages)
        const graphqlCurrentUser = data?.getCurrentUser || currentUser;
        
        // Get other participant
        const otherParticipant = (() => {
          // Handle partial sale conversations - show buyer to seller, seller to buyer
          if (conversation.conversationType === 'partial_sale_listing' && conversation.partialSaleListingUser) {
            const { buyer, seller } = conversation.partialSaleListingUser;
            
            // Current user is seller, show buyer
            if (String(graphqlCurrentUser?.id) === String(seller?.id)) {
              return {
                id: buyer?.id,
                name: buyer?.name,
                allowed_name: buyer?.allowed_name || buyer?.name,
                user_name: buyer?.user_name,
                fb_user_name: buyer?.fb_user_name,
                profile_picture: buyer?.profile_picture || 'advisor.png',
                is_admin: false
              };
            }
            // Current user is buyer, show seller
            if (String(graphqlCurrentUser?.id) === String(buyer?.id)) {
              return {
                id: seller?.id,
                name: seller?.name,
                allowed_name: seller?.allowed_name || seller?.name,
                user_name: seller?.user_name,
                fb_user_name: seller?.fb_user_name,
                profile_picture: seller?.profile_picture || 'advisor.png',
                is_admin: false
              };
            }
          }
          
          // Handle other Paddy conversations (succession readiness, support)
          if (['paddy', 'support_request'].includes(conversation.conversationType)) {
            return {
              id: 'paddy',
              name: 'Paddy',
              allowed_name: 'Paddy',
              profile_picture: 'paddy.png',
              is_admin: false
            };
          }
          
          return conversation.participant1?.id === graphqlCurrentUser?.id 
            ? conversation.participant2 
            : conversation.participant1;
        })();

        // Create a sendMessage function with access to conversation data
        const sendMessageWithData = async (messageData = null) => {          
          // Handle both old format (just text) and new format (messageData object)
          const messageText = messageData?.body || newMessage;
          const attachments = messageData?.attachments || [];
          
          if (!messageText.trim() && attachments.length === 0) {
            return;
          }
          
          if (!subscription.current || isSending) {
            return;
          }

          setIsSending(true);
          setSendError(null);
          
          try {
            if (attachments.length > 0) {
              if (!sendMessageWithAttachments) {
                throw new Error('GraphQL mutation not available for file attachments');
              }
              
              // Convert file objects to the format expected by GraphQL
              const files = attachments.map(attachment => {
                if (!attachment.file) {
                  console.error('Attachment missing file object:', attachment);
                  return null;
                }
                return attachment.file;
              }).filter(Boolean); // Remove null values
              
              if (files.length === 0) {
                throw new Error('No valid files to send');
              }
              
              // Final validation - ensure all files are actual File objects
              const invalidFiles = files.filter(file => !(file instanceof File));
              if (invalidFiles.length > 0) {
                console.error('Invalid file objects detected:', invalidFiles);
                throw new Error(`${invalidFiles.length} invalid file objects detected`);
              }
              
              const result = await sendMessageWithAttachments({
                variables: {
                  conversationId: conversationId,
                  body: messageText.trim() || null,
                  files: files
                }
              });
              
              // Scroll to bottom immediately after sending (ActionCable will also trigger scroll)
              setTimeout(() => scrollToBottom(), 200);
              
            } else {
              // Regular text message via ActionCable
              console.log('[ChatPanel] Sending message via ActionCable:', {
                conversationId,
                messageLength: messageText.trim().length,
                hasSubscription: !!subscription.current,
                isConnected
              });
              
              if (!subscription.current) {
                throw new Error('ActionCable subscription not initialized');
              }
              
              if (!isConnected) {
                throw new Error('Not connected to ActionCable. Please wait or refresh.');
              }
              
              subscription.current.speak({ 
                message: messageText.trim(),
                room_id: conversationId
              });
              
              console.log('[ChatPanel] Message sent via ActionCable');
            }
            
            setNewMessage('');
            inputRef.current?.focus();
            
            
            // Scroll to bottom for text messages too
            setTimeout(() => scrollToBottom(), 100);
            
          } catch (error) {
            console.error('[ChatPanel] Failed to send message:', error);
            // RJ Seller Visibility — the server returns a distinct `conversation_ended`
            // code specifically so the client can explain this case
            // (send_message.rb). Until now nothing read it, so a send into a
            // stopped conversation failed with a generic message.
            const ended = /conversation_ended/.test(error?.message || '');
            alert(ended
              ? 'Messaging is paused for this conversation. The seller can reopen it from their inbox.'
              : `Failed to send message: ${error.message}. Please try again.`);
          } finally {
            setIsSending(false);
          }
        };

        // Handle keyboard shortcuts with access to sendMessageWithData
        const handleKeyPressWithData = (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // For keyboard shortcuts, just send text (no attachments)
            sendMessageWithData();
          }
        };

        return (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 flex flex-col bg-[var(--succession-bg-primary)] overflow-hidden min-h-0">
              {/* Chat Header - Fixed at top */}
              <div className="flex-shrink-0">
                <ChatHeader 
                  conversation={conversation}
                  otherParticipant={otherParticipant}
                  isConnected={isConnected}
                  currentUser={graphqlCurrentUser}
                  onSearchToggle={handleSearchToggle}
                  company={data?.company}
                  conversationId={conversationId}
                  onConversationUpdate={handleConversationUpdate}
                />
              </div>

              {/* Messages Area - Demo style: clean white/light background */}
              <div className="messages-scroll-container flex-1 overflow-y-auto overflow-x-hidden p-6 bg-[var(--succession-bg-primary)]">

              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-[var(--succession-border)] border-t-[#4A90E2] rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.messaging.chat_panel.loading_messages' })}</p>
                  </div>
                </div>
              ) : allMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--succession-bg-secondary)] flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-[var(--succession-text-primary)] mb-2">
                    {intl.formatMessage({ id: 'succession.messaging.chat_panel.start_the_conversation' })}
                  </h3>
                  <p className="text-sm text-[var(--succession-text-secondary)] max-w-xs">
                    {intl.formatMessage({ id: 'succession.messaging.chat_panel.send_first_message' }, { name: get(otherParticipant, 'allowed_name', get(otherParticipant, 'name', 'Unknown')) })}
                  </p>
                </div>
              ) : (
                <>
                  {/* Partial Sale Info - Show for partial sale conversations */}
                  {conversation.conversationType === 'partial_sale_listing' && conversation.partialSaleListingUser && (
                    <div className="mb-4">
                      <PartialSaleInfo
                        partialSaleListingUser={conversation.partialSaleListingUser}
                        currentUser={graphqlCurrentUser}
                      />
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-1">
                    {allMessages.map((message, index) => {
                      const nextMessage = allMessages[index + 1];
                      const isFirst = index === 0 || allMessages[index - 1].sender?.id !== message.sender?.id;
                      const isLastInGroup = !nextMessage || nextMessage.sender?.id !== message.sender?.id;
                      const showDivider = index === 0 || 
                        getTimeDifference(message.createdAt, allMessages[index - 1].createdAt) > 3600000;

                      // Add reactions to message
                      const messageWithReactions = {
                        ...message,
                        reactions: messageReactions[message.id] || []
                      };

                      return (
                        <div key={message.id} data-message-index={index}>
                          <MessageItem
                            message={messageWithReactions}
                            currentUser={graphqlCurrentUser}
                            otherParticipant={otherParticipant}
                            conversation={conversation}
                            isFirst={isFirst}
                            isLastInGroup={isLastInGroup}
                            showDivider={showDivider}
                            showTime={index === 0 || 
                              getTimeDifference(message.createdAt, allMessages[index - 1].createdAt) > 300000
                            }
                            isHighlighted={index === highlightedMessageIndex}
                            nextMessageSender={nextMessage?.sender}
                            onReaction={handleReaction}
                          />
                        </div>
                      );
                    })}
                    
                    {/* Typing Indicator */}
                    <TypingIndicator 
                      typingUsers={typingUsers} 
                      currentUser={graphqlCurrentUser}
                    />
                    
                    <div ref={messagesEndRef} />
                  </div>
                </>
              )}
              </div>

              {/* RJ Seller Visibility Workflow — seller-side action bar.
                  Only renders when the viewer is the succession seller side of a
                  wealth_rj :message conversation with a growth buyer (server-computed
                  via PersonalConversationType#rj_visibility_seller_view). Component
                  renders Invite to Meeting Room / Stop Messaging / Reopen buttons +
                  the NDA nudge modal (triggered by the ActionCable socket flag). */}
              {conversation.rjVisibilitySellerView && (
                <RjSellerConversationActions
                  conversationId={conversationId}
                  conversationEnded={!!conversation.endedAt}
                  ndaNudgeTriggered={rjNudgeTriggered}
                  onNudgeDismissed={() => setRjNudgeTriggered(false)}
                  onEnded={() => { setRjNudgeTriggered(false); refetch(); }}
                  onReopened={() => refetch()}
                  // Returned (not fire-and-forget) so the actions bar can await
                  // the refresh and surface a failure instead of sitting on a
                  // pending state forever. The invite changes a server-computed
                  // field on the OTHER participant, so only a refetch reveals it.
                  onInvited={() => { setRjNudgeTriggered(false); return refetch(); }}
                  // The buyer is the other participant; the state is computed
                  // against them, so the seller's three branches come from one
                  // server value shared with the profile drawer.
                  inviteState={otherParticipant?.rj_meeting_room_invite_state}
                  buyerUserId={otherParticipant?.id}
                  // Drives the deal-room gate on Stop Messaging. Without it the
                  // button would render on threads rj_end_responses refuses.
                  conversationType={conversation?.conversationType}
                />
              )}

              {/* Partial Sale conversations — the same Meeting Room control for the
                  LISTING OWNER (any motivations), with the prompt on their 5th
                  message. Meeting Room only: no Stop Messaging/Reopen. The invite is
                  linked to the listing. Server-computed via
                  PersonalConversationType#ps_meeting_room_seller_view; never true
                  together with rjVisibilitySellerView (that one requires :message). */}
              {conversation.psMeetingRoomSellerView && (
                <RjSellerConversationActions
                  variant="partial_sale"
                  conversationId={conversationId}
                  ndaNudgeTriggered={rjNudgeTriggered}
                  onNudgeDismissed={() => setRjNudgeTriggered(false)}
                  onInvited={() => { setRjNudgeTriggered(false); return refetch(); }}
                  inviteState={conversation.psMeetingRoomInviteState}
                  buyerUserId={conversation.partialSaleListingUser?.buyer?.id}
                  partialSaleListingId={conversation.psMeetingRoomInviteState?.partial_sale_listing_id}
                  conversationType={conversation?.conversationType}
                />
              )}

              {/* RJ Seller Visibility — the stopped state was previously invisible to
                  BOTH parties: the composer silently greyed out with no explanation,
                  and the buyer got no action bar at all. The seller had just been told
                  the *buyer* would be muted, so being unable to type themselves read
                  as a bug. State the truth to whoever is looking. */}
              {sendError && (
                <div
                  className="flex-shrink-0 flex items-start gap-2 px-4 py-2 text-sm text-red-700 bg-red-50 border-t border-red-200"
                  role="alert"
                >
                  <span className="flex-1">{sendError}</span>
                  <button
                    type="button"
                    onClick={() => setSendError(null)}
                    className="flex-shrink-0 font-medium underline"
                    aria-label="Dismiss error"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {!!conversation.endedAt && (
                <div className="flex-shrink-0 px-4 py-2 text-sm text-succession-text-secondary bg-succession-bg-secondary border-t border-succession-border">
                  {String(conversation.endedByUserId) === String(graphqlCurrentUser?.id)
                    ? 'You paused messaging in this conversation. Neither of you can send messages until you reopen it.'
                    : 'Messaging is paused in this conversation. Neither of you can send messages until the seller reopens it.'}
                </div>
              )}

              {/* Message Composer - Demo style: clean bottom area
                  Disabled when the conversation is in the terminal ended state
                  (RJ Seller Visibility Stop Messaging). Seller can Reopen via the
                  RjSellerConversationActions button above; buyer cannot. */}
              <div className="flex-shrink-0 border-t border-[var(--succession-border)] bg-[var(--succession-bg-primary)]">
                <MessageComposer
                  currentUser={graphqlCurrentUser}
                  conversationId={conversationId}
                  onSendMessage={(messageData) => sendMessageWithData(messageData)}
                  onTyping={handleTyping}
                  disabled={conversation.blockedByParticipant1 || conversation.blockedByParticipant2 || !!conversation.endedAt}
                />
              </div>

              {/* Message Search Modal */}
              <MessageSearch
                messages={allMessages}
                currentUser={graphqlCurrentUser}
                conversation={conversation}
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                onResultSelect={handleSearchResult}
              />
            </div>
          </div>
        );
      })()}
    </>
  );
};

/**
 * ChatHeader - Demo-style clean chat header
 */
const ChatHeader = ({
  conversation,
  otherParticipant,
  isConnected,
  currentUser,
  loading = false,
  error = false,
  onSearchToggle,
  company,
  conversationId,
  onConversationUpdate
}) => {
  const intl = useIntl();
  if (loading) {
    return (
      <div className="px-6 py-4 bg-[var(--succession-bg-primary)] flex items-center gap-3 border-b border-[var(--succession-border)]">
        <div className="w-10 h-10 rounded-full bg-[var(--succession-bg-tertiary)] animate-pulse" />
        <div className="flex-1">
          <div className="w-24 h-4 rounded bg-[var(--succession-bg-tertiary)] animate-pulse mb-1" />
          <div className="w-16 h-3 rounded bg-[var(--succession-bg-tertiary)] animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-4 bg-[var(--succession-bg-primary)] flex items-center gap-3 border-b border-[var(--succession-border)]">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
          </svg>
        </div>
        <span className="text-sm text-red-500">{intl.formatMessage({ id: 'succession.messaging.chat_panel.failed_to_load_short' })}</span>
      </div>
    );
  }

  const displayName = otherParticipant?.allowed_name || otherParticipant?.name || 'Unknown User';

  // Check if the other participant has blocked this conversation
  const otherParticipantIsParticipant1 = conversation?.participant1?.id === otherParticipant?.id;
  const blockedByOtherParticipant = otherParticipantIsParticipant1 
    ? conversation?.blockedByParticipant1 
    : conversation?.blockedByParticipant2;

  return (
    <div className="px-6 py-4 bg-[var(--succession-bg-primary)] flex items-center justify-between border-b border-[var(--succession-border)]">
      <div className="flex items-center gap-3">
        {/* Avatar - Demo style: 40px, simple */}
        <div className="relative w-10 h-10 rounded-full overflow-hidden bg-[var(--succession-bg-tertiary)] flex-shrink-0">
          {get(otherParticipant, 'profile_picture') ? (
            <img
              src={getAvatarStyle(get(otherParticipant, 'profile_picture', ''), company).url}
              alt={displayName}
              className="w-full h-full object-cover"
              style={isNewStyleAvatar(get(otherParticipant, 'profile_picture', '')) ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#4A90E2] text-white font-medium text-sm">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Name and Type */}
        <div>
          <h3 className="text-base font-semibold text-[var(--succession-text-primary)] m-0 leading-tight">
            <ParticipantName
              participant={otherParticipant}
              className="text-base font-semibold text-[var(--succession-text-primary)] leading-tight"
            >
              {displayName}
            </ParticipantName>
          </h3>
          {otherParticipant?.advisor_firm_name && !otherParticipant?.is_student && (
            <p className="text-xs text-[var(--succession-text-secondary)] m-0 mt-0.5">
              {otherParticipant.advisor_firm_name}
            </p>
          )}
          <p className="text-xs text-[var(--succession-text-secondary)] m-0 mt-0.5 flex items-center gap-2">
            {(() => {
              const typeInfo = getConversationTypeInfo(conversation?.conversationType, conversation?.company, intl);
              return (
                <span className="flex items-center">
                  {typeInfo.icon}
                  {typeInfo.label}
                </span>
              );
            })()}
            {otherParticipant?.is_student && (
              <span className="text-xs font-semibold leading-none px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                {intl.formatMessage({ id: 'succession.messaging.chat_panel.recruit_badge' })}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Search Button - Demo style */}
        <button
          onClick={onSearchToggle}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-[var(--succession-text-secondary)] hover:bg-[var(--succession-hover-bg)] hover:text-[var(--succession-text-primary)] transition-colors"
          aria-label={intl.formatMessage({ id: 'succession.messaging.chat_panel.search_messages_aria' })}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        {/* Archive Button */}
        {conversationId && (
          <ArchiveButton
            currentUser={currentUser}
            conversationId={conversationId}
            conversation={conversation}
            onConversationUpdate={onConversationUpdate}
          />
        )}

        {/* Block Button - only show if not blocked by other participant */}
        {conversationId && !blockedByOtherParticipant && (
          <BlockButton
            currentUser={currentUser}
            conversationId={conversationId}
            conversation={conversation}
            onConversationUpdate={onConversationUpdate}
          />
        )}
      </div>
    </div>
  );
};

const getConversationTypeInfo = (type, company = null, intl = null) => {
  const iconClass = "w-3.5 h-3.5 inline-block mr-1";
  const t = (id, fallback) => intl ? intl.formatMessage({ id }) : fallback;

  const types = {
    deal: {
      label: getDealRoomBrandedName(company) || t('succession.messaging.chat_panel.deal_room_label', 'Deal Room'),
      icon: <BriefcaseIcon className={iconClass} />
    },
    deal_interest: {
      label: getDealRoomBrandedName(company) || t('succession.messaging.chat_panel.interest_label', 'Interest'),
      icon: <BriefcaseIcon className={iconClass} />
    },
    partial_sale_listing: {
      label: t('succession.messaging.chat_panel.partial_sale_label', 'Partial Sale'),
      icon: <ShoppingCartIcon className={iconClass} />
    },
    support_request: {
      label: t('succession.messaging.chat_panel.support_label', 'Support'),
      icon: <LifebuoyIcon className={iconClass} />
    },
    paddy: {
      label: t('succession.messaging.chat_panel.paddy_label', 'Paddy'),
      icon: <StarIcon className={iconClass} />
    },
    message: {
      label: t('succession.messaging.chat_panel.message_label', 'Message'),
      icon: <ChatBubbleLeftRightIcon className={iconClass} />
    }
  };
  return types[type] || {
    label: t('succession.messaging.chat_panel.message_label', 'Message'),
    icon: <ChatBubbleLeftRightIcon className={iconClass} />
  };
};

// All sub-components are now imported from separate files

ChatPanel.propTypes = {
  currentUser: PropTypes.object,
  conversationId: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  onConversationUpdate: PropTypes.func
};

const ChatPanelWithIntl = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  const locale = window.locale;
  return (
    <IntlProvider locale={locale} messages={translations[locale] || translations.en}>
      <ChatPanel {...props} />
    </IntlProvider>
  );
};

export default ChatPanelWithIntl; 